/**
 * UI-3c Task 3 — CallbackDrawer.
 *
 * The create/edit drawer for a scheduled callback (spec §3.4), hosted inside
 * the shared `ResourceDrawer` (`width="standard"`).
 *
 * Backend contract (apps/api/src/routes/callbacks/schemas.ts)
 * ----------------------------------------------------------
 *   - POST  /callbacks         body: { phoneE164, scheduledAt, agentId?,
 *                                      maxAttempts?, callId? }
 *                              (CreateCallbackRequestSchema — `scheduledAt` is
 *                              an ISO 8601 datetime string and MUST be in the
 *                              future; `maxAttempts` is 1-10; `agentId`/`callId`
 *                              are `.nullable().optional()`).
 *   - PATCH /callbacks/:id     body: { scheduledAt? } | { status: 'CANCELLED' }
 *                              (UpdateCallbackRequestSchema — only `scheduledAt`
 *                              (reschedule) and `status: 'CANCELLED'` are
 *                              accepted; at least one must be present).
 *
 * `callId` (linked call)
 * ----------------------
 * The backend create schema marks `callId` optional. Per spec §3.4, an
 * admin-created callback is not linked to a prior call, so this field is
 * OMITTED from the Phase-1 form entirely (never sent).
 *
 * Save ownership
 * --------------
 * The props interface — `{ open, callback, onClose, onSaved }` — has no
 * `onSave`. The drawer performs the API call itself, then calls `onSaved()`
 * followed by `onClose()`. Errors surface via toast and the drawer stays open.
 *
 *   - create: POST  /callbacks            (full body)
 *   - update: PATCH /callbacks/:id        ({ scheduledAt } — reschedule only)
 *
 * Edit mode (PENDING callbacks only)
 * ----------------------------------
 * In edit mode every field except `scheduledAt` is locked (read-only / disabled
 * and excluded from the validated schema). A separate "Cancel callback" button
 * PATCHes `{ status: 'CANCELLED' }`. Defensively, Save is blocked entirely if
 * the callback is not `PENDING` (the page only opens edit mode for PENDING
 * callbacks, but the drawer enforces it too).
 *
 * Agent dropdown
 * --------------
 * Sourced from `useAgents()` (UI-2b hook). The field is optional — a "No
 * preference" choice (empty value) maps to an unassigned callback.
 *
 * Remount per callback
 * --------------------
 * `ZodForm` mount-initializes from `defaultValues` and does not re-sync. The
 * inner form is given a `key` of `callback?.id ?? 'new'` so opening a different
 * callback — or switching between create and edit — fully remounts it.
 */
import { useRef, useState, type JSX } from 'react'
import { z } from 'zod'
import { Button } from 'primereact/button'
import ResourceDrawer from '../ResourceDrawer.js'
import { ZodForm, FormText, FormCalendar, FormDropdown, FormNumber } from '../../forms/index.js'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import { useAgents } from '../../../hooks/useAgents.js'
import { isValidE164 } from '../../../utils/e164.js'

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

export type CallbackStatus =
  | 'PENDING'
  | 'ORIGINATING'
  | 'COMPLETED'
  | 'NO_ANSWER'
  | 'FAILED'
  | 'CANCELLED'

export interface Callback {
  id: string
  callId: string | null
  phoneE164: string
  agentId: string | null
  status: CallbackStatus
  scheduledAt: string
  maxAttempts: number
  attemptCount: number
  createdAt: string
  updatedAt: string
}

export interface CallbackDrawerProps {
  open: boolean
  /** The callback being edited; `null` = create mode. */
  callback: Callback | null
  onClose: () => void
  /** Called after a successful save so the parent can refetch + close. */
  onSaved: () => void
}

const DEFAULT_MAX_ATTEMPTS = 3
const NO_AGENT = '' // sentinel for the "No preference" dropdown choice

// ---------------------------------------------------------------------------
// Zod schemas
// ---------------------------------------------------------------------------

/** A non-null Date strictly in the future. */
const futureDateField = z
  .date({
    message: 'A scheduled date and time is required',
  })
  // Note: this client-side check may have a small pick-time vs submit-time race;
  // the backend is the source of truth for the future-date constraint.
  .refine((d) => d.getTime() > Date.now(), {
    message: 'The scheduled time must be in the future',
  })

/** CREATE — phone + agent + maxAttempts are all part of the form. */
const CreateCallbackSchema = z.object({
  phoneE164: z
    .string()
    .trim()
    .min(1, 'Phone number is required')
    .refine(isValidE164, {
      message: 'Enter a valid phone number in E.164 format (e.g. +15551234567)',
    }),
  scheduledAt: futureDateField,
  agentId: z.string().optional(),
  maxAttempts: z
    .number({ message: 'Max attempts is required' })
    .int()
    .min(1, 'Max attempts must be at least 1')
    .max(10, 'Max attempts must be 10 or fewer'),
})

/** EDIT — only `scheduledAt` is editable; everything else is locked. */
const EditCallbackSchema = z.object({
  scheduledAt: futureDateField,
})

interface CallbackFormValues {
  phoneE164?: string
  /** `undefined` until the user picks a date (the Zod refine then rejects it). */
  scheduledAt?: Date
  agentId?: string
  maxAttempts?: number
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CallbackDrawer({
  open,
  callback,
  onClose,
  onSaved,
}: CallbackDrawerProps): JSX.Element {
  const toast = useToast()
  const formRef = useRef<HTMLFormElement>(null)
  const [saving, setSaving] = useState(false)
  const { agents } = useAgents()

  const isEdit = callback !== null
  /** Remount key — changes when a different callback (or create mode) opens. */
  const contentKey = callback?.id ?? 'new'

  /** Defensive guard — editing is only valid for PENDING callbacks. */
  const editLocked = isEdit && callback.status !== 'PENDING'

  // Agent dropdown options — a leading "No preference" choice + every agent.
  const agentOptions = [
    { label: 'No preference', value: NO_AGENT },
    ...agents.map((a) => ({ label: `Ext. ${a.extension}`, value: a.id })),
  ]

  /** Read-only agent label shown in edit mode. */
  const lockedAgent = isEdit && callback.agentId ? agents.find((a) => a.id === callback.agentId) : null
  const lockedAgentLabel = lockedAgent
    ? `Ext. ${lockedAgent.extension}`
    : isEdit && callback.agentId
      ? 'Unknown agent'
      : 'No preference'

  const defaultValues: CallbackFormValues = {
    ...(isEdit ? {} : { phoneE164: '' }),
    ...(callback ? { scheduledAt: new Date(callback.scheduledAt) } : {}),
    ...(isEdit
      ? {}
      : { agentId: NO_AGENT, maxAttempts: DEFAULT_MAX_ATTEMPTS }),
  }

  // --- Save flow -----------------------------------------------------------

  async function handleSubmit(values: CallbackFormValues): Promise<void> {
    if (editLocked) {
      toast.error('Only PENDING callbacks can be rescheduled.')
      return
    }
    if (!(values.scheduledAt instanceof Date)) {
      toast.error('A scheduled date and time is required.')
      return
    }
    setSaving(true)
    try {
      if (callback === null) {
        // CREATE — full body. `callId` is intentionally omitted (admin-created).
        const body: Record<string, unknown> = {
          phoneE164: values.phoneE164,
          scheduledAt: values.scheduledAt.toISOString(),
          maxAttempts: values.maxAttempts ?? DEFAULT_MAX_ATTEMPTS,
        }
        if (values.agentId && values.agentId !== NO_AGENT) {
          body.agentId = values.agentId
        }
        await api.post('/callbacks', body)
        toast.success('Callback scheduled')
      } else {
        // EDIT — reschedule only. Skip the PATCH if the time is unchanged.
        const next = values.scheduledAt.toISOString()
        if (next === new Date(callback.scheduledAt).toISOString()) {
          onSaved()
          onClose()
          return
        }
        await api.patch(`/callbacks/${callback.id}`, { scheduledAt: next })
        toast.success('Callback rescheduled')
      }
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save callback.')
    } finally {
      setSaving(false)
    }
  }

  // --- Cancel callback (edit mode) -----------------------------------------

  async function handleCancelCallback(): Promise<void> {
    if (callback === null) return
    setSaving(true)
    try {
      await api.patch(`/callbacks/${callback.id}`, { status: 'CANCELLED' })
      toast.success('Callback cancelled')
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel callback.')
    } finally {
      setSaving(false)
    }
  }

  // --- Render --------------------------------------------------------------

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      onSave={async () => {}}
      formRef={formRef}
      saving={saving}
      disabled={editLocked}
      width="standard"
      title={callback ? 'Edit Callback' : 'New Callback'}
    >
      <ZodForm
        key={contentKey}
        schema={isEdit ? EditCallbackSchema : CreateCallbackSchema}
        defaultValues={defaultValues}
        onSubmit={(data) => handleSubmit(data as CallbackFormValues)}
        formRef={formRef}
      >
        {isEdit ? (
          // Phone is not editable when rescheduling — render it read-only,
          // outside the validated schema.
          <div className="flex flex-col gap-1 mb-3">
            <label htmlFor="callback-phone-readonly" className="text-sm font-medium">
              Phone
            </label>
            <input
              id="callback-phone-readonly"
              type="tel"
              value={callback.phoneE164}
              disabled
              readOnly
              className="border rounded px-2 py-1 bg-gray-100 text-gray-600"
            />
          </div>
        ) : (
          <FormText
            name="phoneE164"
            label="Phone"
            type="tel"
            required
            placeholder="+15551234567"
            helpText="E.164 format — a leading + and country code."
          />
        )}

        <FormCalendar
          name="scheduledAt"
          label="Scheduled at"
          required
          showTime
          hourFormat="24"
          showIcon
          helpText="The callback must be scheduled for a future date and time."
        />

        {isEdit ? (
          // Agent is locked when rescheduling — read-only display.
          <div className="flex flex-col gap-1 mb-3">
            <label htmlFor="callback-agent-readonly" className="text-sm font-medium">
              Agent
            </label>
            <input
              id="callback-agent-readonly"
              type="text"
              value={lockedAgentLabel}
              disabled
              readOnly
              className="border rounded px-2 py-1 bg-gray-100 text-gray-600"
            />
          </div>
        ) : (
          <FormDropdown
            name="agentId"
            label="Agent"
            options={agentOptions}
            helpText="Optional — leave as “No preference” to let any agent take the call."
          />
        )}

        {isEdit ? (
          // Max attempts is locked when rescheduling — read-only display.
          <div className="flex flex-col gap-1 mb-3">
            <label htmlFor="callback-max-attempts-readonly" className="text-sm font-medium">
              Max attempts
            </label>
            <input
              id="callback-max-attempts-readonly"
              type="number"
              value={callback.maxAttempts}
              disabled
              readOnly
              className="border rounded px-2 py-1 bg-gray-100 text-gray-600"
            />
          </div>
        ) : (
          <FormNumber
            name="maxAttempts"
            label="Max attempts"
            min={1}
            max={10}
            helpText="How many times to retry (1–10). Default is 3."
          />
        )}

        {isEdit && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            <Button
              type="button"
              label="Cancel callback"
              severity="danger"
              outlined
              disabled={saving || editLocked}
              onClick={() => {
                void handleCancelCallback()
              }}
            />
            <p className="mt-1 text-sm text-gray-500">
              Cancels the callback so it is no longer attempted.
            </p>
          </div>
        )}
      </ZodForm>
    </ResourceDrawer>
  )
}
